import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import {
	useEventTransport,
	NO_TRANSPORT_CODE,
} from "../hooks/useEventTransport";
import "./EventTransport.css";

const ACK_HELP =
	"Carers must acknowledge this event's transport arrangements. Enter the text they will be asked to acknowledge (1000 characters at most).";

export type TransportValue = {
	code: string;
	notes: string;
	acknowledgementRequired: boolean;
	acknowledgementText: string;
};

interface EventTransportProps {
	// The event's saved Transport object, if any.
	initialData?: {
		Code?: string;
		Notes?: string | null;
		AcknowledgementRequired?: boolean;
		AcknowledgementText?: string | null;
	} | null;
	onValueChange: (value: TransportValue) => void;
}

export const EventTransport: React.FC<EventTransportProps> = ({
	initialData,
	onValueChange,
}) => {
	const { options } = useEventTransport();
	const [value, setValue] = useState<TransportValue>({
		code: initialData?.Code || NO_TRANSPORT_CODE,
		notes: initialData?.Notes || "",
		acknowledgementRequired: initialData?.AcknowledgementRequired === true,
		acknowledgementText: initialData?.AcknowledgementText || "",
	});

	// Blank acknowledgement text falls back to a default naming the method.
	const defaultAckText = (code: string) => {
		const method = options.find((o) => o.id === code)?.label ?? code;
		return `I acknowledge my daughter will be travelling via ${method.toLowerCase()}`;
	};

	const update = (changes: Partial<TransportValue>) => {
		const next = { ...value, ...changes };
		// None has nothing to acknowledge, so the acknowledgement is cleared.
		if (next.code === NO_TRANSPORT_CODE) {
			next.acknowledgementRequired = false;
			next.acknowledgementText = "";
		}
		setValue(next);
		onValueChange({
			...next,
			acknowledgementText: next.acknowledgementRequired
				? next.acknowledgementText.trim() || defaultAckText(next.code)
				: next.acknowledgementText,
		});
	};

	const selected = value.code;
	const notes = value.notes;
	const ackRequired = value.acknowledgementRequired;
	const ackText = value.acknowledgementText;
	const handleSelect = (code: string) => update({ code });
	const handleNotes = (text: string) => update({ notes: text });
	const handleAckRequired = (checked: boolean) =>
		update({ acknowledgementRequired: checked });
	const handleAckText = (text: string) => update({ acknowledgementText: text });

	return (
		<Container>
			<Row className="mb-3">
				<Col className="col-md-12">
					<h3>Transport</h3>
				</Col>
			</Row>
			<div
				className="transport-options"
				role="radiogroup"
				aria-label="Transport"
			>
				{options.map((option) => (
					<button
						type="button"
						key={option.id}
						role="radio"
						aria-checked={selected === option.id}
						className={
							"transport-option" + (selected === option.id ? " selected" : "")
						}
						title={option.description}
						onClick={() => handleSelect(option.id)}
					>
						{option.imageUrl && <img src={option.imageUrl} alt="" />}
						<span>{option.label}</span>
					</button>
				))}
			</div>
			<Form.Group className="mt-4 transport-notes">
				<Form.Label htmlFor="transport_notes">Transport Notes</Form.Label>
				<Form.Control
					as="textarea"
					rows={4}
					maxLength={500}
					id="transport_notes"
					name="TransportNotes"
					value={notes}
					onChange={(e) => handleNotes(e.target.value)}
				/>
			</Form.Group>
			{selected !== NO_TRANSPORT_CODE && (
				<Form.Group className="mt-4 transport-notes">
					<Form.Check
						type="checkbox"
						id="acknowledgement_required"
						label={
							<>
								Acknowledgement required
								<FontAwesomeIcon
									icon={faCircleQuestion}
									className="ms-2 text-secondary"
									title={ACK_HELP}
									aria-label={ACK_HELP}
								/>
							</>
						}
						checked={ackRequired}
						onChange={(e) => handleAckRequired(e.target.checked)}
					/>
					{ackRequired && (
						<Form.Control
							type="text"
							maxLength={1000}
							id="acknowledgement_text"
							name="AcknowledgementText"
							className="mt-2"
							aria-label="Acknowledgement text"
							placeholder={defaultAckText(selected)}
							value={ackText}
							onChange={(e) => handleAckText(e.target.value)}
						/>
					)}
				</Form.Group>
			)}
		</Container>
	);
};
